import { useMemo } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { findProject, getRelatedProjects } from '@/data'
import { useContent } from '@/content'
import { useDocumentMeta } from '@/hooks'
import { ROUTES } from '@/lib/paths'
import { Button, Reveal, Section } from '@/components/ui'
import { ProjectCaseStudy } from '@/features/projects'
import { ProjectNav } from '@/features/projects/ProjectNav'
import { SocialPanels } from '@/features/social'

/**
 * A single case study, resolved from the `:slug` route param.
 *
 * An unknown slug redirects to the work index rather than rendering an empty
 * page, so stale links still land somewhere useful.
 */
export const ProjectDetailPage = () => {
  const { slug } = useParams<{ slug: string }>()
  const { projects, site } = useContent()

  // Prefer the live content list so an admin-added project resolves too,
  // falling back to the compiled lookup.
  const project =
    projects.find((item) => item.slug === slug) ??
    (slug ? findProject(slug) : undefined)

  useDocumentMeta({
    title: project ? `${project.title} — ${site.name}` : site.name,
    description: project?.summary,
  })

  // A fresh random three each time this page is landed on, not reshuffled
  // on every unrelated re-render — the dependency is the project itself, so
  // navigating away and back (or to a different case study) draws again.
  const related = useMemo(
    () => (project ? getRelatedProjects(projects, project.slug, 3) : []),
    [projects, project],
  )

  if (!project) {
    return <Navigate to={ROUTES.projects} replace />
  }

  const index = projects.findIndex((item) => item.slug === project.slug)
  const previous = index > 0 ? projects[index - 1] : undefined
  const next = index < projects.length - 1 ? projects[index + 1] : undefined

  return (
    <>
      <Section spacious>
        <Button to={ROUTES.projects} variant="bare" icon="arrowUpRight">
          All work
        </Button>
        <ProjectCaseStudy project={project} />
      </Section>

      <Reveal>
        <Section>
          <ProjectNav previous={previous} next={next} related={related} />
        </Section>
      </Reveal>

      <Reveal>
        <SocialPanels />
      </Reveal>
    </>
  )
}
